/* dashboardExplanations — mapa de descrições contextuais dos KPIs
 * exibidos no Dashboard institucional. Usado pelo componente
 * <DashboardTooltip> para popup didático (hover / focus / touch).
 *
 * Cada chave (label do KPI) tem:
 *   - titulo:      cabeçalho curto (opcional; usa a chave se ausente)
 *   - explicacao:  o que aquele número significa
 *   - origem:      de onde vem o dado (coleção / endpoint)
 *   - dica:        como interpretar / que ação tomar
 */
export const DASHBOARD_EXPLANATIONS = {
  // ─── KPIs institucionais principais ────────────────────────────────
  "Fontes": {
    explicacao:
      "Quantidade total de fontes cadastradas no acervo. Uma fonte pode ser artigo, relatório, livro, documento institucional, notícia técnica ou literatura cinzenta. Uma mesma fonte pode gerar um ou mais insumos.",
    origem: "MongoDB · coleção `sources` · GET /api/dashboard/summary",
    dica: "Clique no card para abrir o drilldown com a lista de fontes recentes e filtros por tipo.",
  },
  "Sementes": {
    explicacao:
      "Quantidade total de Sementes de Futuro registradas no sistema, independentemente do status de validação.",
    origem: "MongoDB · coleção `future_seeds`",
    dica: "Inclui rascunhos, pendentes, aprovadas e rejeitadas.",
  },
  "Aprovadas": {
    explicacao:
      "Sementes que passaram pela revisão humana e foram aprovadas para compor a base institucional.",
    origem: "future_seeds · status_validacao='aprovado'",
    dica: "Apenas sementes aprovadas devem ser citadas em relatórios institucionais.",
  },
  "Pendentes": {
    explicacao:
      "Sementes aguardando revisão humana. Nenhum registro pendente deve ser considerado aprovado metodologicamente.",
    origem: "future_seeds · status_validacao='pendente'",
    dica: "Priorize revisar os registros mais antigos primeiro. Use o módulo Revisão Humana.",
  },
  "Rejeitadas": {
    explicacao:
      "Sementes recusadas durante a revisão por inconsistência, ausência de evidência, inadequação metodológica ou outro motivo registrado.",
    origem: "future_seeds · status_validacao='rejeitado'",
    dica: "Consulte o motivo da rejeição no detalhe da semente antes de reabrir o registro.",
  },
  "Rascunhos": {
    explicacao:
      "Sementes ainda em preparação pelo pesquisador, não enviadas formalmente para revisão.",
    origem: "future_seeds · status_validacao='rascunho'",
    dica: "Rascunhos ficam visíveis apenas para o próprio pesquisador — só entram no fluxo institucional após envio.",
  },

  // ─── Tendências temporais ──────────────────────────────────────────
  "Últimos 7 dias": {
    explicacao:
      "Quantidade de novos registros criados no período indicado. Usado para acompanhar ritmo de coleta e produção.",
    origem: "future_seeds · agregação por data_criacao",
    dica: "Queda súbita pode indicar ritmo de coleta abaixo do esperado.",
  },
  "Últimos 30 dias": {
    explicacao:
      "Quantidade de novos registros criados no período indicado. Usado para acompanhar ritmo de coleta e produção.",
    origem: "future_seeds · agregação por data_criacao",
    dica: "Compare com meses anteriores para identificar tendência.",
  },
  "Últimos 90 dias": {
    explicacao:
      "Quantidade de novos registros criados no período indicado. Usado para acompanhar ritmo de coleta e produção.",
    origem: "future_seeds · agregação por data_criacao",
    dica: "Bom para acompanhar a produção trimestral do laboratório.",
  },

  // ─── Pipeline V2 (Fonte → Insumo → Semente) ────────────────────────
  "Insumos": {
    explicacao:
      "Trechos extraídos de uma fonte que apresentam sinal prospectivo relevante. O insumo é a ponte entre a fonte original e a Semente de Futuro.",
    origem: "MongoDB · coleção `inputs`",
    dica: "Um insumo pode virar semente (promovido), ser descartado ou permanecer em rascunho.",
  },
  "Insumos em rascunho": {
    explicacao:
      "Insumos identificados, mas ainda não promovidos ou descartados.",
    origem: "inputs · status='rascunho'",
    dica: "Reavalie periodicamente — insumos parados podem ser promovidos ou descartados.",
  },
  "Insumos promovidos": {
    explicacao:
      "Insumos que foram convertidos em Sementes de Futuro.",
    origem: "inputs · status='promovido'",
    dica: "Métrica de conversão do fluxo Fonte → Insumo → Semente.",
  },
  "Insumos descartados": {
    explicacao:
      "Trechos analisados, mas considerados insuficientes ou inadequados para gerar semente.",
    origem: "inputs · status='descartado'",
    dica: "O motivo do descarte deve ser registrado na justificativa.",
  },
  "Sementes via V2": {
    explicacao:
      "Sementes criadas a partir do fluxo metodológico Fonte → Insumo → Semente.",
    origem: "future_seeds · input_id ≠ null",
    dica: "Sementes V2 têm rastreabilidade completa do trecho original.",
  },
  "Score IA médio": {
    explicacao:
      "Média de confiança atribuída pela IA ao identificar insumos prospectivos. Esse score é apenas indicativo e não substitui a revisão humana.",
    origem: "inputs · média de ai_confidence_score",
    dica: "Score alto ≠ semente aprovada. A revisão humana é sempre necessária.",
  },
  "Fontes c/ PDF anexo": {
    explicacao: "Fontes que possuem arquivo PDF arquivado no sistema.",
    origem: "sources · pdf_storage_path ≠ null",
    dica: "Ideal para rastreabilidade científica quando o link original falha.",
  },
  "Fontes c/ link Drive": {
    explicacao: "Fontes vinculadas a arquivos compartilhados no Google Drive.",
    origem: "sources · link_drive ≠ null",
    dica: "Use apenas quando o link original for indisponível.",
  },
  "Sementes c/ Drive excepcional": {
    explicacao: "Sementes cujo acesso à fonte foi feito via link excepcional de Drive (fora do link público original).",
    origem: "future_seeds · link_drive_excepcional ≠ null",
    dica: "Preferir sempre o link original; o Drive excepcional é registro de contingência.",
  },
  "Sementes arquivadas": {
    explicacao: "Sementes movidas para o arquivo institucional — geralmente porque foram substituídas por versão mais recente, tornaram-se obsoletas ou foram desativadas administrativamente.",
    origem: "future_seeds · status_validacao='arquivado'",
    dica: "Registros arquivados não aparecem no fluxo ativo, mas permanecem no acervo histórico.",
  },
  "Checklist 100%": {
    explicacao:
      "Sementes em que o pesquisador marcou todos os itens do checklist operacional.",
    origem: "future_seeds · researcher_checklist (todos true)",
    dica: "Indicador de rigor metodológico do pesquisador.",
  },
  "Taxa de aprovação": {
    explicacao:
      "Percentual de sementes aprovadas em relação ao total de sementes registradas.",
    origem: "aprovadas ÷ total_sementes × 100",
    dica: "Taxas muito altas podem indicar revisão superficial; muito baixas indicam problemas de coleta.",
  },
};

/** Retorna explicação normalizada pelo label do KPI. */
export function getKpiExplanation(label) {
  if (!label) return null;
  return DASHBOARD_EXPLANATIONS[label] || null;
}
